import { toast } from '@heidi/design-system/toast';

import {
  delayUpgradeExtendAction,
  QuotaExceedType,
  shouldShowDelayUpgradeModal,
} from '@/components/modal/delay-upgrade-modal/delayUpgradeAtoms';
import { openQuotaExceededUpgradeModal } from '@/features/settings/billing/utils/openQuotaExceededUpgradeModal';
import {
  GET_DOCUMENT_BY_ID,
  type QueryDocumentResponse,
} from '@/hooks/query/useQueryDocumentById';
import { useIsFreeUser } from '@/hooks/useIsFreeUser';
import { documentCreatedByTemplate } from '@/lib/analytics/scribe/document';
import { paywallModalOpenFromQuotaExceeded } from '@/lib/analytics/scribe/payments';
import { track } from '@/lib/analytics/track';
import { type NoteGenerateTrigger } from '@/lib/telemetry/noteGenerateTracking';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { StreamingStatus } from '@/models/scribeRecordModel';
import { type Dispatch } from '@/store';
import { type ErrorResponse } from '@/types';
import { mapNewBrain } from '@/utils/brainUtils';
import {
  getSessionFromCache,
  setConsultNoteAsFailed,
} from '@/utils/consultNoteStreamHelpers';
import {
  type Brain,
  type GenerationMethod,
  type TemplateCategory,
} from '@heidi/schema/ml-scribe';
import {
  FrontendNoteFailureReason,
  GET_SESSION_BY_ID,
  type QuerySessionResponse,
} from '@heidi/session/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useSetAtom } from 'jotai';
import { useDispatch } from 'react-redux';
import { useQueryUser } from '../query/useQueryUser';
import { useMutateCreateEmptyDocument } from './useMutateCreateEmptyDocument';
import { useMutateRegenerateDocument } from './useMutateRegenerateDocument';

type Props = {
  templateId?: string;
  templateCategory?: TemplateCategory;
  aiCommandId?: string;
  aiCommandText?: string;
  generationMethod: GenerationMethod;
  brain?: Brain;
  name?: string;
  queueOnly?: boolean;
  /** `autonomous` after a stop or upload, `explicit` for a user action. */
  trigger: NoteGenerateTrigger;
};

export default function useMutateCreateDocumentFromNote({
  sessionId,
}: {
  sessionId: string;
}) {
  const dispatch = useDispatch<Dispatch>();
  const { isFreeUser } = useIsFreeUser();

  const { data: userData } = useQueryUser((data) => ({
    brain: mapNewBrain(data?.session_settings?.brain, isFreeUser),
    voiceStyle: data?.session_settings?.default_voice_style,
  }));

  const { mutateAsync: createEmptyDocument } = useMutateCreateEmptyDocument({
    sessionId,
  });
  const { mutateAsync: generateDocumentContent } = useMutateRegenerateDocument({
    sessionId,
  });
  const setDelayUpgradeModalOpen = useSetAtom(shouldShowDelayUpgradeModal);
  const setDelayUpgradeExtendAction = useSetAtom(delayUpgradeExtendAction);

  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['createEmptyDocumentFromNote', { sessionId }],
    mutationFn: async (input: Props) => {
      const documentResponse = await createEmptyDocument({
        ...input,
        brain: userData?.brain,
      });

      if (input.queueOnly) {
        return documentResponse;
      }

      await dispatch.scribeRecordModel._streamResponse({
        sessionID: sessionId,
        generationMethod: input.generationMethod,
        templateId: input.templateId,
        templateCategory: input.templateCategory,
        voiceStyle: userData?.voiceStyle,
        brain: userData?.brain,
        shouldGenerateClinicalTasks: true,
        trigger: input.trigger,
      });

      return documentResponse;
    },
    onSuccess: async ({ documentResponse }, input) => {
      if (input.queueOnly) {
        queryClient.setQueryData<QueryDocumentResponse>(
          [GET_DOCUMENT_BY_ID, { sessionId, documentId: documentResponse.data.id }],
          (data) => {
            if (!data) return data;
            return {
              ...data,
              streamingStatus: StreamingStatus.NOT_STARTED,
              abortController: new AbortController(),
            };
          }
        );
        return;
      }

      queryClient.setQueryData<QuerySessionResponse>(
        [GET_SESSION_BY_ID, { sessionId }],
        (data) => {
          if (data) {
            return {
              ...data,
              isDocumentFromNote: true,
            };
          }
          return data;
        }
      );

      await generateDocumentContent({
        documentId: documentResponse.data.id,
        brain: documentResponse.data.brain,
        trigger: input.trigger,
      });

      // tracking
      documentCreatedByTemplate(input.templateId);

      // V2
      track({
        options: { amplitude: false },
        event_name: TrackingEventName.DocumentCreated,
        props: {
          session_id: sessionId,
          document_id: documentResponse.data.id,
          generation_method: 'template',
          template_id: input.templateId,
        },
      });
    },
    onSettled: () => {
      // void queryClient.invalidateQueries({
      //   queryKey: [
      //     GET_DOCUMENTS_BY_ID,
      //     { sessionId: sessionId },
      //   ],
      // });
    },
    onError: (error) => {
      if (error instanceof AxiosError) {
        const errorData = error.response?.data as ErrorResponse | undefined;
        // Handle both nested (detail.type) and flat (type) error response formats
        const errorType =
          errorData?.detail?.type ?? (errorData as { type?: string })?.type;
        if (errorType === 'UnlimitedQuotaUntilNotSetError') {
          setDelayUpgradeModalOpen(true);
          setDelayUpgradeExtendAction(QuotaExceedType.CREATE_DOCUMENT_FROM_NOTE);
          return;
        }
        if (
          errorType === 'ASKAIQuotaExceedError' ||
          errorType === 'TemplateQuotaExceedError'
        ) {
          const sessionFromCache = getSessionFromCache(sessionId);
          openQuotaExceededUpgradeModal();
          if ((sessionFromCache?.consult_note.result?.length ?? 0) === 0) {
            setConsultNoteAsFailed(
              sessionId,
              FrontendNoteFailureReason.CREATE_DOC_FROM_NOTE_OUT_OF_QUOTA
            );
          }
          paywallModalOpenFromQuotaExceeded();
          return;
        }
        const errorMsg =
          errorData?.detail?.msg ?? (errorData as { msg?: string })?.msg ?? error.message;
        toast({
          tone: 'destructive',
          title: 'Failed to create document',
          description: errorMsg,
        });
      }
    },
  });
}
